/**
 * LocalPulseGrid — the "Your Local Pulse" 4-card row.
 *
 * Cards match reference mock:
 *   Local Stories       — newspaper article illustration
 *   Events Near You     — calendar with heart-shaped pin
 *   Find or Offer Help  — handshake with heart above
 *   Community Resources — stack of books with plant sprout
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";

const CARDS = [
  {
    key: "stories",  title: "Local Stories",       lead: "Real people. Real places. Inspiring stories from your community.",
    to: "/blog",     tint: "from-sky-100 to-blue-200",         iconBg: "bg-blue-500",   accent: "text-blue-700",
  },
  {
    key: "events",   title: "Events Near You",     lead: "Discover community events, meetups and local happenings.",
    to: "/events",   tint: "from-amber-100 to-orange-200",     iconBg: "bg-orange-500", accent: "text-orange-600",
  },
  {
    key: "help",     title: "Find or Offer Help",  lead: "Lend a hand or get support from people near you.",
    to: "/help",     tint: "from-emerald-100 to-teal-200",     iconBg: "bg-emerald-500", accent: "text-emerald-700",
  },
  {
    key: "resources",title: "Community Resources", lead: "Find local services, businesses and helpful resources.",
    to: "/organisations", tint: "from-violet-100 to-purple-200", iconBg: "bg-violet-500", accent: "text-violet-700",
  },
];

function readLocality() {
  try {
    const raw = localStorage.getItem("sp:locality");
    if (raw) return JSON.parse(raw);
  } catch { /* noop */ }
  return null;
}

export default function LocalPulseGrid() {
  const [loc, setLoc] = useState(readLocality);
  useEffect(() => {
    const t = setInterval(() => setLoc(readLocality()), 4000);
    return () => clearInterval(t);
  }, []);

  const cityLabel =
    (loc && (loc.city || loc.name || loc.label)) || "Your community";
  const regionLabel =
    (loc && (loc.region || loc.state || loc.country)) || null;
  const localityDisplay = regionLabel ? `${cityLabel}, ${regionLabel}` : cityLabel;

  return (
    <section className="bg-white py-10 sm:py-14" data-testid="sp-local-pulse">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl">
              Your Local Pulse
            </h2>
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700"
              data-testid="sp-locality-chip"
            >
              <MapPin className="w-3.5 h-3.5 text-blue-600" />
              {localityDisplay}
            </span>
          </div>
          <Link
            to="/locality"
            data-testid="sp-see-more-locality"
            className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 text-sm font-semibold"
          >
            See more in {cityLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CARDS.map((c) => <LocalCard key={c.key} card={c} />)}
        </div>
      </div>
    </section>
  );
}

function LocalCard({ card }) {
  return (
    <Link
      to={card.to}
      data-testid={`sp-local-card-${card.key}`}
      className="group flex flex-col bg-white rounded-2xl overflow-hidden ring-1 ring-slate-200 hover:ring-blue-300 hover:shadow-lg transition-all"
    >
      <div className={`relative h-40 bg-gradient-to-br ${card.tint} overflow-hidden`}>
        <CardArt kind={card.key} />
      </div>
      <div className="p-5 pt-4 relative">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className={`font-serif font-bold text-slate-900 text-lg ${card.accent}`}>
              {card.title}
            </h3>
            <p className="mt-1 text-sm text-slate-600 leading-snug">
              {card.lead}
            </p>
          </div>
          <span className={`w-8 h-8 grid place-items-center rounded-full bg-slate-50 ring-1 ring-slate-200 ${card.accent} shrink-0 group-hover:bg-blue-50 group-hover:ring-blue-200 transition-colors`}>
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function CardArt({ kind }) {
  const Scene = SCENES[kind] || SCENES.stories;
  return (
    <svg viewBox="0 0 300 160" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
      <Scene />
    </svg>
  );
}

const SCENES = {
  stories: () => (
    <g transform="translate(75 20)">
      {/* newspaper page */}
      <rect x="0" y="0" width="150" height="120" rx="6" fill="white" />
      <rect x="0" y="0" width="150" height="16" fill="#1e3a8a" />
      <text x="75" y="11" fontFamily="Georgia,serif" fontSize="7" fontWeight="700" fill="white" textAnchor="middle" letterSpacing="1.5">STORIES</text>
      {/* hero image on article */}
      <rect x="10" y="24" width="130" height="42" fill="#dbeafe" rx="2" />
      <polygon points="10,58 40,42 70,54 100,36 140,50 140,66 10,66" fill="#3b82f6" opacity="0.55" />
      <circle cx="125" cy="42" r="4" fill="#fbbf24" />
      {/* headline */}
      <rect x="10" y="74" width="100" height="4" rx="1" fill="#0f172a" opacity="0.9" />
      <rect x="10" y="82" width="80"  height="3" rx="1" fill="#334155" opacity="0.7" />
      {/* body */}
      <rect x="10" y="94"  width="130" height="2.5" rx="1" fill="#475569" opacity="0.55" />
      <rect x="10" y="100" width="130" height="2.5" rx="1" fill="#475569" opacity="0.55" />
      <rect x="10" y="106" width="105" height="2.5" rx="1" fill="#475569" opacity="0.55" />
    </g>
  ),

  events: () => (
    <g transform="translate(90 22)">
      {/* calendar body */}
      <rect x="0" y="10" width="120" height="105" rx="8" fill="white" />
      <rect x="0" y="10" width="120" height="24" fill="#ea580c" rx="8" />
      <rect x="0" y="24" width="120" height="10" fill="#ea580c" />
      {/* rings on top */}
      <rect x="24" y="0"  width="6" height="18" rx="2" fill="#7c2d12" />
      <rect x="90" y="0"  width="6" height="18" rx="2" fill="#7c2d12" />
      {/* month text */}
      <text x="60" y="26" fontFamily="Arial" fontSize="7" fontWeight="700" fill="white" textAnchor="middle" letterSpacing="1.5">EVENTS</text>
      {/* grid dots */}
      {[0,1,2,3,4].map((c) => [0,1,2].map((r) => (
        <circle key={`${c}-${r}`} cx={20 + c * 20} cy={50 + r * 18} r="2" fill="#fed7aa" />
      )))}
      {/* heart pin */}
      <g transform="translate(48 55)">
        <path d="M12 4 C 8 -2, 0 0, 0 8 C 0 16, 12 26, 12 26 C 12 26, 24 16, 24 8 C 24 0, 16 -2, 12 4 Z" fill="#dc2626" />
        <circle cx="12" cy="10" r="4" fill="white" />
      </g>
      {/* sparkles */}
      <g fill="#fbbf24">
        <polygon points="8,52 10,55 8,58 6,55" />
        <polygon points="108,68 110,72 108,76 106,72" />
      </g>
    </g>
  ),

  help: () => (
    <g transform="translate(60 20)">
      {/* heart above */}
      <path d="M90 20 C 82 8, 62 12, 62 28 C 62 42, 90 60, 90 60 C 90 60, 118 42, 118 28 C 118 12, 98 8, 90 20 Z" fill="#dc2626" />
      {/* burst rays */}
      {[[68,10],[112,10],[52,32],[128,32]].map(([x,y], i) => (
        <line key={i} x1="90" y1="10" x2={x} y2={y} stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
      ))}
      {/* two hands shaking */}
      <g transform="translate(0 70)">
        {/* left sleeve */}
        <path d="M0 30 L 40 30 L 55 20 L 55 45 L 40 45 L 0 45 Z" fill="#1e40af" />
        {/* left hand */}
        <path d="M55 20 L 90 25 L 90 45 L 55 45 Z" fill="#fbbf24" />
        {/* right sleeve */}
        <path d="M180 30 L 140 30 L 125 20 L 125 45 L 140 45 L 180 45 Z" fill="#059669" />
        {/* right hand */}
        <path d="M125 20 L 90 25 L 90 45 L 125 45 Z" fill="#fbbf24" />
        {/* thumb wrap */}
        <ellipse cx="90" cy="30" rx="7" ry="10" fill="#f59e0b" opacity="0.85" />
      </g>
    </g>
  ),

  resources: () => (
    <g transform="translate(80 18)">
      {/* books stack */}
      <rect x="0"  y="65" width="140" height="18" fill="#3b82f6" rx="1" />
      <rect x="8"  y="47" width="124" height="18" fill="#dc2626" rx="1" />
      <rect x="16" y="29" width="108" height="18" fill="#059669" rx="1" />
      {/* spine stripes */}
      <rect x="4"  y="70" width="132" height="3" fill="white" opacity="0.55" />
      <rect x="12" y="52" width="116" height="3" fill="white" opacity="0.55" />
      <rect x="20" y="34" width="100" height="3" fill="white" opacity="0.55" />
      {/* letter marks */}
      <text x="70" y="79" fontFamily="Georgia,serif" fontSize="8" fontWeight="700" fill="white" textAnchor="middle">C</text>
      <text x="70" y="61" fontFamily="Georgia,serif" fontSize="8" fontWeight="700" fill="white" textAnchor="middle">O</text>
      <text x="70" y="43" fontFamily="Georgia,serif" fontSize="8" fontWeight="700" fill="white" textAnchor="middle">M</text>
      {/* plant sprout */}
      <path d="M70 30 C 66 20, 58 18, 58 8 C 66 8, 70 14, 70 20" fill="#22c55e" />
      <path d="M70 30 C 74 20, 82 18, 82 8 C 74 8, 70 14, 70 20" fill="#16a34a" />
      <rect x="69" y="20" width="2" height="12" fill="#166534" />
      {/* bookmark ribbon */}
      <polygon points="118,29 128,29 128,52 123,47 118,52" fill="#fbbf24" />
    </g>
  ),
};
